import React, { useEffect, useState } from "react";
import { Row, Col, FormGroup, Button } from "reactstrap";

import { useFormik } from "formik";
import * as Yup from "yup";

import { LabelText } from "../Utils/Input";
import InputField from "../Utils/InputField";
import SelectField from "../Utils/SelectField";
import CommonButton from "../Utils/CommonButton";
import { Loader } from "../Utils/Loader";

import { useDispatch, useSelector } from "react-redux";

import { getItems } from "../../slices/Item/GetItem/thunk";
import { getAllLocation } from "../../slices/Location/GetLocation/thunk";
import { createSO } from "../../slices/SO/CreateSO/thunk";
import { updateSO } from "../../slices/SO/UpdateSO/thunk";
import { approvedRequestSO } from "../../slices/SO/ApprovedRequestSO/thunk";
import { deleteNotification } from "../../slices/Notification/DeleteNotification/thunk";
import { toast } from "react-toastify";

function SOForm({ closeModal, refreshSOList, editData, isEdit, isApprove }) {
  const dispatch = useDispatch();
  const { items } = useSelector((state) => state.GetItems || {});
  const { locations = [] } = useSelector((state) => state.locations);

  const [loading, setLoading] = useState(false);
  const [apiloading, apisetLoading] = useState(false);
  const currencySymbol = localStorage.getItem("currencySymbol");

  useEffect(() => {
    dispatch(getItems(10000, 1));
  }, [dispatch]);

  useEffect(() => {
    dispatch(getAllLocation(apisetLoading));
  }, [dispatch]);

  const itemNameOptions = (currentIndex) => {
    const selectedItems = values.items
      .filter((_, idx) => idx !== currentIndex)
      .map((item) => item?.itemName?.value);

    return (
      (items?.items || [])
        .filter((item) => !selectedItems.includes(item.itemname))
        .map((item) => ({
          label: item.itemname,
          value: item.itemname,
          itemid: item.itemid,
          sku: item.sku,
          unitprice: item.unitprice,
          itemcount: item.itemcount,
        })) || []
    );
  };

  const statusOptions = [
    { label: "Received", value: "Received" },
    { label: "In Transit", value: "In Transit" },
    { label: "Drafted", value: "Drafted" },
  ];

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: editData || {
      locationName: null,
      //   status: null,
      items: [],
    },

    validationSchema: Yup.object({
      locationName: Yup.object().required("Required"),
      // status: Yup.object().required("Required")

      items: Yup.array()
        .of(
          Yup.object().shape({
            // sku: Yup.object().required("SKU is required"),

            itemName: Yup.object().required("Item Name is required").nullable(),

            orderedQty: Yup.number()
              .typeError("Ordered Quantity must be a number")
              .required("Ordered Quantity is required")
              .min(1, "Minimum 1")
              .test(
                "max-net-qty",
                "Ordered Quantity should not exceed Net Quantity",
                function (value) {
                  const { itemCount } = this.parent;
                  return Number(value) <= Number(itemCount);
                },
              ),

            approvedQty: Yup.number()
              .typeError("Approved Quantity must be a number")
              .integer("Only whole numbers allowed")
              .min(1, "Minimum 1")
              .when([], {
                is: () => isApprove,
                then: (schema) =>
                  schema.required("Approved Quantity is required"),
                otherwise: (schema) => schema.notRequired(),
              }),

            unitPrice: Yup.number()
              .typeError("Unit Price must be a number")
              .required("Unit Price is required")
              .min(0, "Cannot be negative"),

            total: Yup.number()
              .typeError("Total must be a number")
              .required("Total is required")
              .min(0, "Cannot be negative"),
          }),
        )
        .min(1, "At least one item is required"),
    }),

    onSubmit: async (values) => {
      const invalidItem = values.items.find(
        (item) => Number(item.orderedQty) > Number(item.itemCount),
      );

      if (invalidItem) {
        toast.error("Ordered Quantity should not exceed Available Quantity");
        return;
      }

      const item = values.items?.[0];

      if (isApprove) {
        if (Number(item?.approvedQty) > Number(item?.orderedQty)) {
          toast.error(
            "Approved Quantity cannot be greater than Ordered Quantity",
          );
          return;
        }

        const payload = {
          approvedquantity: Number(item?.approvedQty || 0),
          orderedquantity: Number(item?.orderedQty || 0),
        };

        dispatch(
          updateSO(
            payload,
            editData?.orderId,
            item?.itemId,
            setLoading,
            () => {
              dispatch(
                approvedRequestSO(
                  { status: "approved" },
                  editData?.orderId,
                  item?.itemId,
                  setLoading,
                  async () => {
                    if (item?.notificationid) {
                      await dispatch(
                        deleteNotification(item?.notificationid, setLoading),
                      );
                    }

                    closeModal();
                    if (refreshSOList) {
                      refreshSOList();
                    }
                  },
                ),
              );
            },
            false,
          ),
        );

        return;
      }

      if (isEdit) {
        const item = values.items?.[0];

        const payload = {
          approvedquantity: Number(item?.approvedQty || 0),
          orderedquantity: Number(item?.orderedQty || 0),
        };

        dispatch(
          updateSO(
            payload,
            editData?.orderId,
            item?.itemId,
            setLoading,
            () => {
              closeModal();
              if (refreshSOList) {
                refreshSOList();
              }
            },
            true,
          ),
        );

        return;
      }

      const payload = {
        fromlocation: values.locationName?.value || "",
        // status: values.status?.value || "",
        soitems: values.items.map((item) => ({
          itemid: item.itemName?.itemid || "",
          itemname: item.itemName?.value || "",
          sku: item.sku || "",
          orderedquantity: Number(item.orderedQty || 0),
          approvedquantity: 0,
          pendingquantity:
            Number(item.orderedQty || 0) - Number(item.deliveredquantity || 0),
          unitprice: Number(item.unitPrice || 0),
          totalprice: Number(item.total || 0),
        })),
      };

      dispatch(
        createSO(payload, setLoading, () => {
          closeModal();

          if (refreshSOList) {
            refreshSOList();
          }
        }),
      );
    },
  });

  const { values, errors, touched, setFieldValue, handleSubmit } = formik;

  const addItem = () => {
    setFieldValue("items", [
      ...values.items,
      {
        itemName: null,
        sku: "",
        orderedQty: "",
        approvedQty: "",
        itemCount: 0,
        unitPrice: "",
        total: "",
      },
    ]);
  };

  const removeItem = (index) => {
    const updated = [...values.items];
    updated.splice(index, 1);
    setFieldValue("items", updated);
  };

  const handleItemChange = (index, field, value) => {
    const updated = [...values.items];

    if (field === "orderedQty") {
      const netQty = Number(updated[index].itemCount || 0);

      if (Number(value) > netQty) {
        toast.error(
          `Ordered Quantity cannot exceed Available Quantity (${netQty})`,
        );
        return;
      }
    }

    updated[index][field] = value;

    const qty = Number(updated[index].orderedQty || 0);
    const price = Number(updated[index].unitPrice || 0);

    updated[index].total = qty * price;

    setFieldValue("items", updated);
  };

  const handleItemNameChange = (index, selectedOption) => {
    const updated = [...values.items];

    updated[index].itemName = selectedOption;
    updated[index].sku = selectedOption?.sku || "";
    updated[index].unitPrice = selectedOption?.unitprice || 0;
    updated[index].itemCount = selectedOption?.itemcount || 0;

    const qty = Number(updated[index].orderedQty || 0);
    const price = Number(updated[index].unitPrice || 0);

    updated[index].total = qty * price;

    setFieldValue("items", updated);
  };

  return (
    <>
      {loading && <Loader />}
      <form onSubmit={handleSubmit}>
        <Row className="gy-3">
          <Col md={12}>
            <FormGroup>
              <LabelText
                htmlFor="locationName"
                displayText="Location Name"
                className={"text-danger"}
                important={"*"}
              />

              <SelectField
                value={values.locationName}
                placeholder="Select Location"
                options={
                  locations?.map((loc) => ({
                    label: loc.name,
                    value: loc.locationid,
                  })) || []
                }
                handleChange={(val) => setFieldValue("locationName", val)}
                isRVStyle={true}
                isDisabled={isEdit}
                className={
                  touched.locationName && errors.locationName
                    ? "error-input"
                    : ""
                }
              />
            </FormGroup>
          </Col>

          <div className="d-flex justify-content-between align-items-center mt-2">
            <h5 className="fw-bold m-0">Products</h5>

            {(!isEdit && !isApprove) && (
              <Button
                type="button"
                className="rv-primary-btn px-3 rounded-1 py-0 d-flex align-items-center"
                style={{
                  fontSize: "13px",
                  minWidth: "70px",
                  height: "30px",
                  lineHeight: "1",
                }}
                onClick={addItem}
              >
                <i className="ri-add-line me-1"></i>
                Add Product
              </Button>
            )}
          </div>

          {/* EMPTY STATE */}
          <div className="px-3">
            {values.items.length === 0 ? (
              <div className="border border-1 rounded-3 p-3 mt-3 bg-light w-100 text-center">
                <p className="mb-0 text-muted fw-semibold">
                  No product added yet. Click "Add Product" to begin.
                </p>
              </div>
            ) : (
              values.items.map((item, index) => (
                <div
                  key={index}
                  className="border border-1 rounded-3 p-3 mt-3 bg-light w-100"
                >
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="m-0">Product {index + 1}</h6>

                    {values.items.length > 0 && !isEdit && (
                      <i
                        className="ri-delete-bin-line text-danger fs-5 cursor-pointer"
                        onClick={() => removeItem(index)}
                      />
                    )}
                  </div>

                  <Row className="gy-3">
                    <Col md={6}>
                      <FormGroup>
                        <LabelText
                          displayText={"Product Name"}
                          className={"text-danger"}
                          important={"*"}
                        />

                        <SelectField
                          value={item.itemName}
                          placeholder="Select Product"
                          options={itemNameOptions(index)}
                          handleChange={(val) =>
                            handleItemNameChange(index, val)
                          }
                          isRVStyle={true}
                          isDisabled={isApprove}
                          className={
                            touched.items?.[index]?.itemName &&
                            errors.items?.[index]?.itemName
                              ? "error-input"
                              : ""
                          }
                        />
                      </FormGroup>
                    </Col>

                    <Col md={6}>
                      <FormGroup>
                        <LabelText
                          displayText={"SKU"}
                          className={"text-danger"}
                          important={"*"}
                        />

                        <InputField
                          value={item.sku || ""}
                          placeholder="SKU"
                          disabled
                        />
                      </FormGroup>
                    </Col>

                    {/* ORDER QTY */}
                    <Col md={6}>
                      <FormGroup>
                        <LabelText
                          displayText={"Ordered Quantity"}
                          className={"fw-semibold text-dark mb-2"}
                          className={"text-danger"}
                          important={"*"}
                        />

                        <InputField
                          type="text"
                          min={1}
                          value={item.orderedQty}
                          disabled={isApprove}
                          onKeyDown={(e) => {
                            if (e.key === "-" || e.key === "e") {
                              e.preventDefault();
                            }
                          }}
                          onWheel={(e) => e.target.blur()}
                          handleChange={(e) => {
                            const value = e.target.value;

                            if (value === "" || Number(value) >= 0) {
                              handleItemChange(index, "orderedQty", value);
                            }
                          }}
                          className={
                            touched.items?.[index]?.orderedQty &&
                            errors.items?.[index]?.orderedQty
                              ? "error-input"
                              : ""
                          }
                        />
                      </FormGroup>
                    </Col>

                    {/* Approved QTY */}

                    {isApprove ? (
                      <Col md={6}>
                        <FormGroup>
                          <LabelText
                            displayText={"Approved Quantity"}
                            className={"text-danger"}
                            important={"*"}
                          />

                          <InputField
                            type="text"
                            value={item.approvedQty}
                            handleChange={(e) => {
                              const value = e.target.value;
                              if (value === "") {
                                handleItemChange(index, "approvedQty", "");
                                return;
                              }
                              if (!/^\d+$/.test(value)) return;
                              handleItemChange(index, "approvedQty", value);
                            }}
                            onKeyDown={(e) => {
                              if (["e", "E", "+", "-", "."].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                          />
                        </FormGroup>
                      </Col>
                    ) : null}

                    <Col md={6}>
                      <FormGroup>
                        <LabelText
                          displayText={"Available Quantity"}
                          className={"fw-semibold text-dark mb-2"}
                          className={"text-danger"}
                          important={"*"}
                        />

                        <InputField
                          type="number"
                          value={item.itemCount}
                          disabled
                        />
                      </FormGroup>
                    </Col>

                    {/* UNIT PRICE */}
                    <Col md={6}>
                      <FormGroup>
                        <LabelText
                          displayText={`Unit Price (${currencySymbol})`}
                          className={"fw-semibold text-dark mb-2"}
                          className={"text-danger"}
                          important={"*"}
                        />

                        <InputField
                          type="number"
                          value={item.unitPrice}
                          disabled
                          handleChange={(e) =>
                            handleItemChange(index, "unitPrice", e.target.value)
                          }
                          className={
                            touched.items?.[index]?.unitPrice &&
                            errors.items?.[index]?.unitPrice
                              ? "error-input"
                              : ""
                          }
                        />
                      </FormGroup>
                    </Col>

                    {/* TOTAL */}
                    <Col md={6}>
                      <FormGroup>
                        <LabelText
                          displayText={`Total (${currencySymbol})`}
                          className={"fw-semibold text-dark mb-2"}
                          className={"text-danger"}
                          important={"*"}
                        />

                        <InputField
                          type="number"
                          value={item.total}
                          disabled
                          className={
                            touched.items?.[index]?.total &&
                            errors.items?.[index]?.total
                              ? "error-input"
                              : ""
                          }
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </div>
              ))
            )}
          </div>

          {/* BUTTONS */}
          <div
            className="d-flex justify-content-center w-100 gap-2 flex-wrap pe-1 py-3"
            style={{
              position: "sticky",
              bottom: 0,
              backgroundColor: "#fff",
              borderTop: "1px solid #e9ebec",
              marginTop: "20px",
            }}
          >
            <div className="d-flex gap-2 justify-content-center">
              <CommonButton
                outline={true}
                className={
                  "btn-label rv-secondary-btn rounded-1 px-1 ps-4 py-1 w-100 w-md-auto text-nowrap"
                }
                color={"main-color"}
                type={"button"}
                buttonAction={() => {
                  closeModal();
                }}
                buttonText={"Cancel"}
                icon={
                  <i className="ri-close-fill label-icon secondary-label-icon align-middle fs-16 me-2"></i>
                }
                btnTxtClsName={"px-2 ps-3 ms-2 me-1 fw-bold"}
              />

              <CommonButton
                color={"main-color"}
                className={
                  "btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1 w-100 w-md-auto text-nowrap"
                }
                icon={
                  <i className="ri-checkbox-circle-line label-icon align-middle fs-16 me-2"></i>
                }
                btnTxtClsName={"px-2 ps-3 ms-2 me-1 fw-bold"}
                type={"submit"}
                buttonText={"Submit"}
              />
            </div>
          </div>
        </Row>
      </form>
    </>
  );
}

export default SOForm;
